import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Users, Calendar, CreditCard, Store, TrendingUp } from "lucide-react";

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    API.get("/admin/stats")
      .then(r => setStats(r.data))
      .catch(err => setError(err.response?.data?.detail || "Acces non autorise"));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]" data-testid="admin-error">
        <div className="text-center">
          <p className="text-lg font-semibold text-destructive">Acces refuse</p>
          <p className="text-sm text-muted-foreground mt-1">{error}</p>
          <p className="text-xs text-muted-foreground mt-4">Connexion admin: admin@cutit.app / admin123</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8]" data-testid="admin-page">
      <div className="max-w-6xl mx-auto p-6 lg:p-10">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold tracking-tight">Admin CutIt</h1>
          <p className="text-sm text-muted-foreground mt-1">Tableau de bord administrateur</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {[
            { label: "Barbiers", value: stats.total_users, icon: Users, color: "text-blue-600" },
            { label: "Salons actifs", value: stats.active_barbers, icon: Store, color: "text-green-600" },
            { label: "Reservations", value: stats.total_bookings, icon: Calendar, color: "text-orange-600" },
            { label: "Abonnes Pro", value: stats.pro_subscriptions, icon: CreditCard, color: "text-purple-600" },
            { label: "MRR", value: `${stats.mrr.toFixed(0)}EUR`, icon: TrendingUp, color: "text-primary" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-border p-5" data-testid={`admin-stat-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <s.icon size={18} className={s.color} />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{s.label}</span>
              </div>
              <p className="font-mono text-3xl font-bold">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Recent Users */}
        <div className="bg-white rounded-xl border border-border">
          <div className="p-5 border-b border-border">
            <h2 className="font-semibold">Inscriptions recentes</h2>
          </div>
          {stats.recent_users?.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Aucun utilisateur</div>
          ) : (
            <div className="divide-y divide-border">
              {stats.recent_users?.map((u, i) => (
                <div key={i} className="p-4 flex items-center justify-between" data-testid={`admin-user-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center font-heading font-bold text-primary text-xs">
                      {u.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{u.name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-muted-foreground">{u.created_at?.split("T")[0]}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
